// ============================================================
// Último costo registrado de un producto (rótulo del costo precargado en Compras)
// ============================================================
// Al agregar un producto a una compra, el campo de costo se precarga con el
// cost_price del maestro. Ese número NO es el de la factura: es el último costo
// que tenía el producto. Este módulo arma el rótulo "Último costo: $X · 23/10 ·
// compra" para que se lea como referencia y no como dato de la factura.
//
// Origen del costo:
//   - 'compra'       → coincide con la última compra no anulada que incluyó el
//                      producto, y esa compra no es anterior al último cambio
//                      registrado en product_cost_history.
//   - 'manual'       → el último cambio vino de otro lado (ficha del producto,
//                      Gestión de costos, importación, recetas).
//   - 'sin_registro' → no hay compras ni historial del producto (costo inicial).
//
// Ojo: el trigger de product_cost_history graba siempre source='manual' (las
// compras no setean app.cost_change_source), así que el origen se deduce
// cruzando historial y compras, no leyendo `source`. En Gastify el trigger no
// existe: sin historial, el origen sale solo de la última compra.

const TOLERANCIA_COSTO = 0.01;
// La compra graba el cost_price después del RPC, unos segundos más tarde que
// compras.created_at. Margen holgado para compras lentas o con muchos renglones.
const VENTANA_COMPRA_MS = 15 * 60 * 1000;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const instante = (v) => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

const mismoCosto = (a, b) => a !== null && b !== null && Math.abs(a - b) <= TOLERANCIA_COSTO;

/**
 * Costo unitario con el que entró `productId` en los renglones de una compra.
 * Si aparece en más de un renglón, toma el último. Las compras viejas pueden
 * no tener `unit_cost`: se cae a `costo_unitario_final` / `costo`.
 */
export function costoDeProductoEnCompra(items, productId) {
  if (!Array.isArray(items) || !productId) return null;
  let costo = null;
  for (const it of items) {
    if (!it || String(it.product_id) !== String(productId)) continue;
    const c = num(it.unit_cost ?? it.costo_unitario_final ?? it.costo);
    if (c !== null) costo = c;
  }
  return costo;
}

/**
 * Decide costo, fecha y origen del último costo.
 * @param {object} p
 * @param {number} p.costoActual        products.cost_price
 * @param {{costo:number, fecha:string}|null} p.ultimoCambio   última fila de product_cost_history
 * @param {{costo:number, fecha:string, createdAt:string}|null} p.ultimaCompra  última compra con el producto
 *        (fecha = fecha del comprobante; createdAt = cuándo se registró)
 * @returns {{costo:number, origen:'compra'|'manual'|'sin_registro', fecha:string|null}}
 */
export function resolverUltimoCosto({ costoActual, ultimoCambio = null, ultimaCompra = null }) {
  const costo = num(costoActual) ?? 0;
  const compraCoincide = ultimaCompra && mismoCosto(num(ultimaCompra.costo), costo);
  const cambioCoincide = ultimoCambio && mismoCosto(num(ultimoCambio.costo), costo);

  if (compraCoincide) {
    const tCompra = instante(ultimaCompra.createdAt || ultimaCompra.fecha);
    const tCambio = cambioCoincide ? instante(ultimoCambio.fecha) : null;
    // Si el último cambio de costo es claramente posterior a la compra, alguien
    // lo volvió a poner a mano en el mismo valor después: gana el cambio.
    const compraEsLaUltima = tCambio === null || tCompra === null || tCompra >= tCambio - VENTANA_COMPRA_MS;
    if (compraEsLaUltima) {
      return { costo, origen: 'compra', fecha: ultimaCompra.fecha || ultimaCompra.createdAt || null };
    }
  }

  if (cambioCoincide) return { costo, origen: 'manual', fecha: ultimoCambio.fecha || null };
  if (!ultimoCambio && !ultimaCompra) return { costo, origen: 'sin_registro', fecha: null };
  // Hubo compras o cambios, pero ninguno explica el costo actual (p. ej. una
  // importación masiva sin historial): es manual, sin fecha confiable.
  return { costo, origen: 'manual', fecha: null };
}

const ORIGEN_LABEL = {
  compra: 'compra',
  manual: 'editado',
  sin_registro: 'costo inicial',
};

/** "23/10" a partir de un timestamptz. Vacío si no hay fecha. */
export function fechaCorta(value) {
  const t = instante(value);
  if (t === null) return '';
  return new Date(t).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

/** Texto del rótulo de origen: "compra 23/10", "editado 12/10", "costo inicial". */
export function describirOrigen(info) {
  if (!info) return '';
  const base = ORIGEN_LABEL[info.origen] || '';
  const f = fechaCorta(info.fecha);
  return f ? `${base} ${f}` : base;
}

/**
 * Trae lo necesario para resolver el último costo de un producto.
 * Tres consultas chicas en paralelo; si alguna falla, se resuelve con lo que haya.
 */
export async function fetchUltimoCosto(supabase, { organizationId, productId }) {
  const [prodRes, histRes, compraRes] = await Promise.all([
    supabase.from('products').select('cost_price').eq('id', productId).maybeSingle(),
    supabase
      .from('product_cost_history')
      .select('cost_price_nuevo, changed_at')
      .eq('product_id', productId)
      .order('changed_at', { ascending: false })
      .limit(1),
    supabase
      .from('compras')
      .select('fecha, created_at, items')
      .eq('organization_id', organizationId)
      .not('estado', 'in', '("ANULADO","ANULADA")')
      .contains('items', JSON.stringify([{ product_id: productId }]))
      .order('created_at', { ascending: false })
      .limit(1),
  ]);

  if (prodRes.error || !prodRes.data) return null;

  const hist = histRes.error ? null : histRes.data?.[0];
  const compra = compraRes.error ? null : compraRes.data?.[0];

  return resolverUltimoCosto({
    costoActual: prodRes.data.cost_price,
    ultimoCambio: hist ? { costo: hist.cost_price_nuevo, fecha: hist.changed_at } : null,
    ultimaCompra: compra
      ? { costo: costoDeProductoEnCompra(compra.items, productId), fecha: compra.fecha, createdAt: compra.created_at }
      : null,
  });
}
